import { useTranslation } from 'react-i18next';
import InfoListItem from './InfoListItem';
import InfoTooltip from './InfoTooltip';
import PriceInfoBlock from './PriceInfoBlock';
import styles from '../../../styles/aprt.module.scss';

function InfoBlock({
	isMobile,
	apartmentViewMode,
	apartmentData,
	selectedCompartmentInd,
	tiersData,
	projectSetup,
}) {
	const { t } = useTranslation();

	const { floorplan_insolation: isInsolationEnabled } = projectSetup.apartments;
	const { are_apartment_prices_visible: arePricesVisible } =
		projectSetup.website;

	//TODO: move to utility functions
	const capitalizeFirstWord = (str) => {
		const words = str.toLowerCase().split(' ');
		if (words.length > 0) {
			words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
		}
		return words.join(' ');
	};
	const edificeTiers = tiersData?.filter(
		(tier) => tier.edifice === apartmentData.edifice_name
	);
	const apartmentTierName = edificeTiers?.find(
		(tier) => tier.ref_tier === apartmentData.ref_tier
	)?.name;
	const apartmentTierNameCapitalized = apartmentTierName
		? capitalizeFirstWord(apartmentTierName)
		: apartmentTierName;

	const selectedCompartment = apartmentData.compartments.find(
		(compartment) => compartment.number === selectedCompartmentInd
	);
	const selectedCompartmentName = selectedCompartment?.name_original; //TODO: do not use name_original, use "name_cad" (ie. idBedroom) to find localized name in compartments.json

	//TODO: move formatArea to utils:
	const formatArea = (num) => {
		if (num) {
			const roundedNumber = num.toFixed(2);
			const localeString = (number) =>
				number.toLocaleString('pl-PL', {
					minimumFractionDigits: 2,
				});

			return localeString(+roundedNumber);
		}
		return '';
	};

	const convertNumberToHHMM = (num) => {
		const milliseconds = num * 60 * 60 * 1000;
		const timeString = new Date(milliseconds).toISOString().slice(11, 16);
		let [hours, minutes] = timeString.split(':');
		hours = parseInt(hours, 10);

		return `${hours}:${minutes}`;
	};

	const convertArrayToHoursRange = (arr) => {
		if (!arr || arr.length === 0) {
			return t('lack_of');
		}
		const ranges = arr.map((range) => {
			const [start, end] = range;
			return `${convertNumberToHHMM(start)} - ${convertNumberToHHMM(end)}`;
		});
		return ranges.join(', ');
	};

	const sumSunLightRanges = (arr) => {
		if (!arr || arr.length === 0) return 0;
		let sum = 0;
		arr.forEach((range) => {
			sum += range[1] - range[0];
		});
		return sum;
	};

	const calculateWindowAreaRatio = (window, floor) => {
		if (!window || window === '0') {
			return t('lack_of');
		} else {
			const windowRatio = (window / floor / 0.125) * 100;
			return `${windowRatio.toFixed(0)}%`;
		}
	};

	const parseDataToRenderApartmentInfo = () => {
		//TODO: add cases for different modes
		//info mode case:
		return [
			{ label: t('availability'), value: apartmentData.availability },
			{ label: t('surface'), value: formatArea(apartmentData.area_original) },
			{ label: t('rooms'), value: apartmentData.rooms },
			{ label: t('tier'), value: apartmentTierNameCapitalized },
			{ label: t('gate'), value: apartmentData.gate },
			...(isInsolationEnabled
				? [
						{
							label: t('insolation'),
							value: convertNumberToHHMM(
								sumSunLightRanges(apartmentData.sun_light_ranges)
							),
						},
				  ]
				: []),
			{
				label: t('exposition'),
				value: apartmentData.exposition ? apartmentData.exposition.join(', ') : t('lack_of'),
			},
		];
	};
	const parseDataToRenderCompartmentInfo = () => {
		//TODO: add cases for different modes
		//info mode case:
		return [
			{
				label: t('surface'),
				value: formatArea(selectedCompartment.area_original),
			},
			...(isInsolationEnabled
				? [
						{
							label: t('insolation'),
							value: convertNumberToHHMM(
								sumSunLightRanges(selectedCompartment.sun_light_ranges)
							),
						},
						{
							label: t('sun_hours'),
							value: convertArrayToHoursRange(
								selectedCompartment.sun_light_ranges
							),
						},
				  ]
				: []),
			{
				label: t('exposition'),
				value: selectedCompartment.exposition ? selectedCompartment.exposition.join(', ') : t('lack_of'),
			},
			{
				label: t('windows'),
				value:
					calculateWindowAreaRatio(
						selectedCompartment.window_area,
						selectedCompartment.area_original
					) || t('lack_of'),
			},
		];
	};

	return (
		<>
			<div className={styles.infoList__block}>
				{!selectedCompartmentInd && !isMobile && (
					<>
						<dl className={styles.infoList__list}>
							{parseDataToRenderApartmentInfo().map((datum, index) => (
								<InfoListItem
									key={index}
									label={datum.label}
									value={datum.value}
								/>
							))}
						</dl>
						{arePricesVisible && (
							<PriceInfoBlock apartmentData={apartmentData} />
						)}
					</>
				)}
				{!selectedCompartmentInd && isMobile && (
					<>
						<div className={styles.list__row}>
							<h3 className={styles.heading__compartments}>Dane lokalu</h3>
						</div>
						<dl className={styles.infoList__list}>
							{parseDataToRenderApartmentInfo().map((datum, index) => (
								<InfoListItem
									key={index}
									label={datum.label}
									value={datum.value}
								/>
							))}
						</dl>
					</>
				)}
				{selectedCompartmentInd && (
					<>
						<div className={styles.list__row}>
							<h3 className={styles.heading__compartments}>
								{selectedCompartmentName}
								{/*//TODO: do not use name_original in selectedCompartmentName, use "name" (ie. idBedroom) to find localized name in compartments.json*/}
							</h3>
						</div>
						<dl className={styles.infoList__list}>
							{parseDataToRenderCompartmentInfo().map((datum, index) => (
								<InfoListItem
									key={index}
									label={datum.label}
									value={datum.value}
								/>
							))}
						</dl>
					</>
				)}
			</div>
			<InfoTooltip />
		</>
	);
}

export default InfoBlock;
